/**
 * components/locks/ConnectorMeshOffset.tsx — Correzione robusta di
 * posizione basata sul bounding box REALE del GLB (25-09-2026, indagine
 * incastro — sostituisce le costanti misurate a mano tipo "-5.495mm").
 *
 * PROBLEMA: la mesh GLB reale non e' simmetrica rispetto alla propria
 * origine (verificato piu' volte con bounding box reali in questa
 * indagine). Le formule di posizionamento (`connectorPlacement.ts`,
 * `connectorFlush.ts`) assumono un modulo centrato sulla propria origine
 * (come il box fallback, l'unico caso disponibile prima degli asset IFC):
 * con la mesh reale il pezzo finisce spostato rispetto al punto calcolato.
 *
 * PERCHE' QUI (render layer) E NON in `model/`/`transforms/`: solo qui si
 * puo' caricare il GLB (SPEC §6 vieta a `model/`/`transforms/` di toccare
 * three.js/asset — vedi `hooks/useLockScene.ts`, che gira FUORI dal
 * `<Canvas>` e non puo' sospendersi su un GLB). La correzione e' quindi
 * composta ESCLUSIVAMENTE qui, mai mutando `ResolvedScene` (SPEC §6, AC-7 —
 * stesso principio gia' usato per `explodeFactor`/notch).
 *
 * COME: il bounding box del GLB grezzo (frame NATIVO, nessuna rotazione
 * applicata) da' il centro geometrico reale relativo alla propria origine.
 * Ruotato dal quaternion GIA' RISOLTO dell'istanza (`instance.quaternion` —
 * lo stesso per mesh e fori, quindi la correzione risultante e' quella
 * corretta per QUALUNQUE rotazione), da' lo scostamento mondiale reale.
 * Sottraendolo dalla posizione calcolata (mesh E viti, stesso valore),
 * mesh e fori restano sincronizzati (corpo rigido) mentre la posizione
 * finale combacia con l'intento della formula (es. "top del connettore
 * flush con top trave") invece di essere spostata dall'asimmetria reale.
 *
 * SOLO ALTEZZA (25-09-2026, bug trovato subito dopo la prima versione): il
 * bbox grezzo ha l'origine fuori centro anche sull'asse spessore (lo
 * sappiamo da tutta l'indagine di oggi — origine su una faccia, non al
 * centro). Applicare la correzione su TUTti e 3 gli assi reintroduceva uno
 * spostamento X in conflitto con la formula esplicita di flush in
 * `connectorPlacement.ts` (che quello spessore lo gestisce gia', o
 * deliberatamente non lo gestisce affatto). Qui si isola quindi SOLO la
 * componente Y (altezza, frame nativo) del centro — l'unica asimmetria che
 * questo meccanismo deve correggere; X/Z restano SEMPRE 0, di competenza
 * esclusiva delle formule esplicite.
 *
 * COMPONENT EULER (25-09-2026, bug trovato con la famiglia 50/75/100/125 —
 * mesh autorate con l'orientamento fisico opposto rispetto alla famiglia
 * piccola, corretto con `component_rotation_deg` su quegli asset):
 * `rawCenter` e' misurato nel frame NATIVO del GLB, PRIMA di
 * `meshQuaternion` (l'euler del componente, applicato SOLO al corpo mesh —
 * vedi `ConnectorMesh.tsx`). Il vero scostamento mondiale del corpo va
 * quindi ruotato PRIMA da `meshQuaternion` (per portarlo nel frame del
 * gruppo, lo stesso dei fori) e SOLO DOPO da `quaternion` (gruppo→mondo) —
 * ruotare con solo `quaternion` (come prima di questo fix) dava il segno
 * sbagliato per gli asset con `component_rotation_deg` non identico.
 *
 * Component pattern: render-prop, stesso schema Suspense-safe di
 * `GlbView`/`GlbViewInner` (guard su url null PRIMA di un componente
 * interno che chiama `useGLTF` incondizionatamente).*/

import { useGLTF } from "@react-three/drei";
import { useMemo } from "react";
import * as THREE from "three";

const ZERO_OFFSET = new THREE.Vector3(0, 0, 0);

export interface ConnectorMeshOffsetProps {
  /** GLB URL blob; null → nessuna correzione (box fallback, gia' centrato).*/
  url: string | null;
  /** Render-prop: riceve il centro REALE (frame nativo, non ruotato) del bbox.*/
  children: (rawCenter: THREE.Vector3) => React.ReactNode;
}

/**
 * Calcola il centro del bounding box REALE del GLB (frame nativo) e lo
 * espone al chiamante via render-prop. `url` null → centro zero (nessuna
 * correzione, il fallback box e' gia' centrato sulla propria origine).*/
export function ConnectorMeshOffset({ url, children }: ConnectorMeshOffsetProps) {
  if (!url) return <>{children(ZERO_OFFSET)}</>;
  return <ConnectorMeshOffsetInner url={url}>{children}</ConnectorMeshOffsetInner>;
}

function ConnectorMeshOffsetInner({
  url,
  children,
}: {
  url: string;
  children: (rawCenter: THREE.Vector3) => React.ReactNode;
}) {
  const { scene } = useGLTF(url);
  const rawCenter = useMemo(() => {
    //Sonda scollegata dalla scena (nessun genitore): il bbox riflette SOLO
    //la geometria nel proprio frame nativo, senza position/quaternion
    //dell'istanza applicati altrove nel render tree.
    const probe = scene.clone(true);
    probe.updateMatrixWorld(true);
    const center = new THREE.Box3().setFromObject(probe).getCenter(new THREE.Vector3());
    //SOLO Y (altezza): X/Z (spessore/larghezza) restano di competenza delle
    //formule esplicite in connectorPlacement.ts — vedi doc-comment in cima.
    center.x = 0;
    center.z = 0;
    return center;
  }, [scene]);
  return <>{children(rawCenter)}</>;
}

/**
 * Applica la correzione: ruota `rawCenter` — PRIMA dal `meshQuaternion`
 * (l'euler del componente, porta il centro nativo nel frame del gruppo) e
 * POI dal `quaternion` GIA' RISOLTO dell'istanza (gruppo→mondo) — e la
 * sottrae dalla posizione. Stessa formula per mesh e viti (chiamata
 * separatamente per ciascuna, con lo stesso `rawCenter`/`quaternion`/
 * `meshQuaternion` del connettore proprietario), cosi' restano sincronizzati.
 *
 * @param position Posizione calcolata (mesh o vite), da correggere.
 * @param rawCenter Centro bbox reale (frame nativo, da `ConnectorMeshOffset`).
 * @param quaternion Quaternion GIA' RISOLTO del connettore proprietario (gruppo→mondo).
 * @param meshQuaternion Euler del componente (`ConnectorInstance.meshQuaternion`,
 * frame nativo→gruppo) — identita' per gli asset senza correzione (nessun
 * cambiamento di comportamento in quel caso).
 * @returns Posizione corretta (tuple, pronta per la prop `position`).*/
export function applyConnectorMeshOffset(
  position: readonly [number, number, number],
  rawCenter: THREE.Vector3,
  quaternion: THREE.Quaternion,
  meshQuaternion: THREE.Quaternion,
): [number, number, number] {
  if (rawCenter.lengthSq() < 1e-9) return [position[0], position[1], position[2]];
  const totalQuaternion = quaternion.clone().multiply(meshQuaternion);
  const correction = rawCenter.clone().applyQuaternion(totalQuaternion);
  return [position[0] - correction.x, position[1] - correction.y, position[2] - correction.z];
}
